/* eslint-disable max-statements,max-lines */
import {FULFILLED} from 'redux-promise-middleware';
import type {SagaIterator} from 'redux-saga';
import {push} from 'connected-react-router';

import {PRODUCT, CART} from 'constants/actions';
import {RECENTLY_VIEWED_STORE} from 'constants/category';
import {CartItemType} from 'constants/flow/flowTypesCart';
import type {ActionType} from 'constants/flow/flowTypes';

import {waitForFulfilled} from 'libs/utils/utils';
import {put, putResolve, takeEvery, call, fork, select, cancel, all} from 'redux-saga/effects';
import {
    getActiveProduct,
    getIsProductFulfilled,
    isContactLenses,
    getProductChildren,
} from 'selectors/product/product';
import {getProduct, setActiveProductId, getProductWizard} from 'actions/product/product';
import {setPageInfo} from 'actions/routerInfo/routerInfo';
import {isConfigurePage as isConfigurePageSelector} from 'selectors/routerInfo/routerInfo';
import {set} from 'reducers/product/previousProductIdentifier';
import {getPreviousProductIdentifier} from 'selectors/product/previousProductIdentifier';
import {localStore} from 'libs/storage/storage';
import {fetchQuoteItemV2} from 'actions/cart/cart';
import {getMyFitFilters} from 'features/myFit/selectors/myFit';
import {FRAME_SIZES} from 'features/myFit/constants/constants';
import flags from 'constants/flags/flags';

import {fetchProductReviews, productReviewsSaga} from './reviews/reviews';
import {displayBanner} from './banner/banner';

/**
 * Send product wizard action
 * @param {number} productId - product id
 */
export function* sendContactsProductWizardAction(productId: number): SagaIterator {
    const isContacts = yield select(isContactLenses);
    if (!isContacts) return;
    const getProductWizardAction = yield call(getProductWizard, productId);
    yield putResolve(getProductWizardAction);
}

/**
 * Save product id for slider recently viewed
 */
export function* saveProductAsRecentlyViewed(): SagaIterator {
    const recentlyViewed = yield call([localStore, 'get'], RECENTLY_VIEWED_STORE, []);
    yield call(waitForFulfilled, getIsProductFulfilled, `${PRODUCT.GET_PRODUCT}_${FULFILLED}`);
    const activeProduct = yield select(getActiveProduct);

    if (recentlyViewed.find(item => item === activeProduct.id)) yield cancel();

    if (recentlyViewed.length >= 5) recentlyViewed.splice(0, 1);

    yield call([localStore, 'set'], RECENTLY_VIEWED_STORE, [...recentlyViewed, activeProduct.id]);
}

/**
 * Listener for change simple product (by color or size)
 */
export function* productChange(): SagaIterator {
    const isConfigurePage = yield select(isConfigurePageSelector);
    const product = yield select(getActiveProduct);
    const previousProductIdentifier = yield select(getPreviousProductIdentifier);

    if ((!product || product.id === previousProductIdentifier) && !isConfigurePage) yield cancel();

    const setPreviousProductIdentifierAction = yield call(set, product.id);
    yield put(setPreviousProductIdentifierAction);

    yield fork(sendContactsProductWizardAction, product.id);

    yield fork(saveProductAsRecentlyViewed);
}

/**
 * Get product for configure page
 * @param {ActionType<CartItemType>} payload - payload from request
 */
export function* getQuoteItemInfo({
    payload: {data: currentQuoteItem},
}: ActionType<CartItemType>): SagaIterator {
    yield put(setPageInfo({page: 'configure'}));
    yield call(displayBanner, currentQuoteItem.product.id);
    const getProductAction = yield call(getProduct, currentQuoteItem.product.id);
    yield putResolve(getProductAction);

    const setActiveProductAction = yield call(setActiveProductId, currentQuoteItem.product.id);
    yield put(setActiveProductAction);
    yield fork(saveProductAsRecentlyViewed);
    yield call(fetchProductReviews, currentQuoteItem.product.id);
}

/**
 * Go to cart page
 */
export function* goToNextPage(): SagaIterator {
    yield put(push('/checkout/cart'));
}

/**
 * Check size in My Fit before change the product
 */
export function* checkMyFitBeforeProductChange({payload: productId}): SagaIterator {
    if (!flags.myFit.isEnabled()) {
        yield put(setActiveProductId(productId));
        return;
    }

    const [children, myFitFilters] = yield all([
        select(getProductChildren),
        select(getMyFitFilters),
    ]);

    // on the CTP children is array, and on the PDP children is object
    const colorId = (Array.isArray(children)
        ? children.find(({id}) => id === productId)
        : children[productId]
    ).color.id;
    const sameColorChildren = Object.values(children).filter(({color: {id}}) => id === colorId);
    const newId =
        sameColorChildren.find(({frame: {size: {label}}}) =>
            myFitFilters.frame_size.map(sizeId => FRAME_SIZES[sizeId]).includes(label),
        )?.id || null;

    yield put(setActiveProductId(newId || productId));
}

/**
 * Start product saga
 */
export default function* productSaga(): SagaIterator {
    yield takeEvery(PRODUCT.SET_ACTIVE_PRODUCT_ID, productChange);
    yield takeEvery(PRODUCT.CHECK_IS_PRODUCT_ID_IN_MY_FIT, checkMyFitBeforeProductChange);
    yield takeEvery(`${fetchQuoteItemV2}_${FULFILLED}`, getQuoteItemInfo);
    yield takeEvery(`${CART.ADD_TO_CART}_${FULFILLED}`, goToNextPage);
    yield fork(productReviewsSaga);
}
